import type { Messages } from '../messages.js'

/**
 * Turkish. `taş` for tiles, which is what a Turkish board game calls them, and `çevirme` for
 * flips: the act of turning one, which is what the meter charges for.
 *
 * The plural forms are deliberately identical. Turkish keeps a noun singular after a numeral,
 * so `3 kelime` is right and `3 kelimeler` is a mistake a translator makes once.
 */
export const tr: Messages = {
  tag: 'tr',

  readingDictionary: 'Sözlük okunuyor…',
  noWordList: '"{language}" için kelime listesi yok. Bir tane oluşturun:  pnpm dictionary build',
  emptyWordList: '"{language}" için kelime listesi boş.',

  flips: 'çevirme',
  score: 'puan',
  words: 'kelime',
  round: 'tur',
  ticksLeftLabel: 'Bu turda kalan süre',
  typeAWord: 'bir kelime yazın',
  tapPrompt: 'harfleri seçmek veya geri vermek için dokunun, sonra {action}',

  boardOfTiles: '{n} taşlık tahta',
  faceDown: 'kapalı',
  wildCard: 'joker',
  wildKey: 'herhangi bir harf',
  letterReplaced: '{from} yerine {to} geldi',
  letterSwap: 'HARF DEĞİŞTİ!',
  spentTile: 'kullanılmış taş',
  hiddenWhilePaused: 'duraklatıldığı için gizli',
  letterInWord: '{letter}, kelimenin {position}. harfi',

  completeWord: 'Kelimeyi tamamla',

  completeShort: 'Tamamla',
  reset: 'Sıfırla',
  pause: 'Duraklat',
  resume: 'Sürdür',
  newGame: 'Yeni oyun',
  paused: 'Duraklatıldı',
  outOfFlips: 'Çevirme kalmadı',
  finalResult: '{rounds} boyunca {words} ile {score} puan',
  playAgain: 'Tekrar oyna',
  share: 'Paylaş',
  shareCopied: 'Kopyalandı.',
  shareSelect: 'Şunu kopyalayın:',

  lettersSelect: 'harfler seçer',
  keysWild: 'hiçbir taşta olmayan bir harf yazdığınızda alınır',
  clearsEvery: 'seçili bütün {letter} harflerini siler',
  undoLastLetter: 'son harfi geri al',
  noWordsYet: 'Henüz kelime yok.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'zaten bulundu',
  reasonTooShort: 'çok kısa',
  reasonNotAWord: 'kelime değil',
  reasonAllFound: 'hepsini zaten buldunuz',
  noSuchLetterUp: 'açık {letter} yok',
  nothingUp: 'açık değil',
  letterHidden: 'bir harf saklandı',
  shuffled: 'karıldı',
  shuffledAndBilled: 'karıldı, kullanılmayan {flips} çevirme düşüldü',
  tooFewLetters: 'Kalan harf sayısı çok az',

  gameLanguage: 'dil',
  interfaceLanguage: 'arayüz',
  dictionarySize: '{full} kelimenin {common} tanesi gündelik',
  filterLanguages: 'Dil ara',
  noMatches: 'Sonuç yok',

  nerdMode: 'inek modu',
  rules: 'Kurallar',
  difficulty: 'zorluk',
  difficultyNames: { easy: 'kolay', medium: 'orta', hard: 'zor', insane: 'çılgın' },
  pausedNoBoard: 'Bu oyun liderlik tablosuna girmez.',
  about: 'Hakkında',
  themeLabel: 'tema',
  themeNames: { traditional: 'geleneksel', light: 'aydınlık', contrast: 'yüksek karşıtlık' },
  tiles: 'taş (N)',
  secondsPerTick: 'saniye / vuruş',
  holdTicks: 'bekleme vuruşu',
  minWord: 'en kısa kelime',
  startingFlips: 'başlangıç çevirmesi',
  wildChance: 'joker olasılığı',
  replaceChance: 'harf değişme olasılığı',
  hideChance: 'harf saklanma olasılığı',
  wordCompleteMode: 'kelime bitince',
  wordCompleteNames: { shuffle: 'karıştır', spend: 'harca', keep: 'sakla' },
  flipEconomy: 'çevirme ekonomisi',
  flipEconomyNames: {
    none: 'yok',
    perLetter: 'harf başına',
    fibonacci: 'fibonacci',
    overMinimum: 'asgarinin üstü',
  },
  repeatedLetterKey: 'tekrar eden harf tuşu',
  keySchemeNames: { cycle: 'döngü', advance: 'ilerle' },
  keySchemeHelp: {
    cycle:
      'A bir sonraki boş A’yı alır, hepsi kelimeye girdikten sonra da onları siler. ' +
      'Shift+A da siler.',
    advance: 'A bir sonraki boş A’yı alır. Shift+A kelimedeki bütün A’ları siler.',
  },

  whatThatMeans: 'Bu ne demek',
  factRound: 'tur',
  factWholeBoardUp: 'tahtanın tamamı açık',
  factRoundCosts: 'bir turun bedeli',
  factFlipsBuy: 'başlangıç çevirmeleri şunu alır',
  factThisBoard: 'bu tahta',
  factBoardHadToAdmit: 'tahtanın kabul etmesi gereken',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, en uzunu {longest}',
  wordsIncludingOneOf: '{words}, biri {ceiling} dahil',
  scorelessRounds: '{rounds} puansız',

  whatAWordPays: 'Bir kelime ne kazandırır',
  columnLetters: 'harf',
  columnCost: 'bedel',
  columnPoints: 'puan',
  columnFlips: 'çevirme',
  columnNet: 'net',

  canonicalRules: 'Standart {difficulty} kuralları.',
  customRules: 'Hazır ayardan değiştirildi. Kendi kurallarınızla alınan puanlar sıralanmaz.',
  applyAndStart: 'Uygula ve yeni oyun başlat',
  changesNextGame: 'Değişiklikler sonraki oyunda geçerli olur.',
  presets: 'Hazır ayarlar:',

  start: 'Başla',
  restart: 'Yeniden başlat',
  quit: 'Çık',
  quitTitle: 'Bu oyundan çıkılsın mı?',
  restartTitle: 'Bu oyun yeniden başlatılsın mı?',
  restartConfirm: 'Yeniden başlat',
  quitConfirm: 'Çık',
  keepPlaying: 'Oynamaya devam et',
  personalBest: 'En iyi oyunlarınız',
  newPersonalBest: 'Yeni bir kişisel rekor.',
  columnRank: '#',
  notRanked: 'Kendi kurallarınız, bu yüzden bu oyun sıralanmaz.',
  notRankedPaused: 'Saat durdu, bu yüzden bu oyun sıralanmaz.',
  rankOfTotal: '{total} içinde {rank}',

  howToPlay: 'Nasıl oynanır',

  backToGame: 'Oyuna dön',
  welcomeTitle: 'Blinkered’a hoş geldiniz',
  tutorialSkip: 'Atla',
  tutorialNext: 'İleri',
  tutorialBack: 'Geri',
  tutorialStart: 'Oynamaya başla',
  tutorialHideAgain: 'Bunu bir daha gösterme',
  tutorialProgress: '{total} içinde {n}',
  tutorialSkipTitle: 'Tanıtım atlansın mı?',
  tutGoalTitle: 'Blinkered bir yarıştır',
  tutGoalBody:
    'Oyuna belirli sayıda çevirmeyle başlarsınız ve dönen her taş bir çevirmeye mal olur. Çevirmeler bitince oyun biter. Kelimeler çevirme kazandırır ve kelime uzadıkça daha çok çevirme kazanırsınız.',
  tutSpentBody:
    'Bir kelimede kullandığınız harfler bir sonraki tura kadar kapalı kalır. Tahtadaki harfleri yalnızca harf değişimi değiştirir.',
  tutChangeTitle: 'Harfler değişebilir',
  tutSameLetters:
    'Oynamaya yetişemediğiniz bir kelime mi gördünüz? Aynı harfler bir sonraki turda, yeni yerlere karılmış olarak geri gelir.',
  tutPickLetters: 'İstediğiniz harflere sırayla dokunarak bir kelime kurun.',
  tutMoreTurn: 'Siz düşünürken taşlar dönmeye devam eder, daha iyi bir harf hâlâ gelebilir.',
  tutTapBack:
    'İstemediğiniz birine mi dokundunuz? Geri vermek için tekrar dokunun. Sonuncusu değil, ' +
    'herhangi biri.',
  tutComplete: 'Kelime hazır olunca Tamamla’ya basın.',
  htBoardTitle: 'Tahta',
  htBoardBody: 'Taşlar rastgele sırayla, teker teker açılır. Açılan taşlardan kelime kurarsınız.',
  htWordsTitle: 'Kelimeler',
  htWordsBody: 'Açık taşlardan bir kelime kurmak için harfleri sırayla yazın veya tıklayın.',
  htFlipsTitle: 'Çevirmeler',
  htFlipsBody:
    'Dönen her taş bir çevirmeye mal olur. Tamamlanan bir kelime toplamınıza çevirme ekler, uzun kelimeler daha çok verir. Çevirmeler bitince oyun biter.',
  htRoundTitle: 'Tur',
  htRoundBody:
    'Turun son taşı döndüğünde bütün tahta bir an açık kalır. Sonra taşlar kapatılıp karılır ve yeni bir tur başlar.',
  htLanguagesTitle: 'Diller',
  htLanguagesBody:
    '{n} dil. Her tahta, insanların gerçekten kullandığı kelimelerle çözülebilir. Sıra dışı bir kelime de puan getirir, yeter ki sözlük onu bilsin.',
  htKeysTitle: 'Klavye',
  htWildTitle: 'Jokerler',
  htWildBody:
    'Bazen harf yerine bir joker çıkar. Joker, geçerli bir kelime kuran herhangi bir harf sayılır. Daha önce tamamladığınız bir kelime sayılmaz.',
  htHideTitle: 'Saklanan harfler',
  htHideBody:
    'Daha zor seviyelerde açık bir harf kendiliğinden geri kapanabilir ve turun ilerisinde yeniden açılır. Sayaç bunun için kırmızı bir çentik kazanır. Seçtiğiniz harfler geri kapanmaz.',
  tutHideBody:
    'Daha zor seviyelerde açık bir harf kendiliğinden geri kapanabilir ve turun ilerisinde yeniden açılır. Seçtiğiniz harfler geri kapanmaz.',
  htSwapTitle: 'Değişen harfler',
  htSwapBody:
    'Bazen turlar arasında bir harfin yerine başka bir harf gelir. Hangi harfin kaldırıldığını ve hangisinin eklendiğini görürsünüz.',
  htLevelsTitle: 'Seviyeler',
  htLevelEasy:
    'Oyun boyunca aynı on iki harf, yani onları öğrenip aklınızda bir kelime listesi tutabilirsiniz. Taşlar yavaş döner ve dolu tahta, harflerinizi rahatça seçmeye yetecek kadar açık kalır.',
  htLevelMedium:
    'Ara sıra bir harf değişir, böylece sonraya sakladığınız kelimeleri akılda tutmak zorlaşır. Bakmaya da düşünmeye de daha az vakit.',
  htLevelHard:
    'Üç harfli kelimeler artık saymaz ve iki turda bir harf değişir. Tahta daha yeni açılmışken karılır.',
  htLevelInsane: 'Hepsi bir arada, tam hızda. Tahta, son çevirmeden hemen sonra karılır.',
  htTouchTitle: 'Dokunmatik ekran',
  htTouchBody:
    'Açık bir taşa dokunarak harfini alın. Aldığınız bir harfe dokunarak geri verin. Tamamla ve Sıfırla tahtanın altında.',

  accountTitle: 'Hesabın',
  menuProfile: 'Profilim',
  menuGames: 'Oyunlarım',
  menuPublicPage: 'Herkese açık sayfam',
  menuSignedInAs: 'Giriş yapıldı:',
  signIn: 'Giriş yap',
  signOut: 'Çıkış yap',
  signInTitle: 'Giriş yap veya kaydol',
  signInLead: 'Hesap oyunlarını saklar, oynadığın her cihazda.',
  signInKeepGame: 'Bu oyunu ve sonrakilerin hepsini saklamak için giriş yap.',
  continueWithApple: 'Apple ile devam et',
  continueWithGoogle: 'Google ile devam et',
  signInOr: 'veya',
  emailLabel: 'E-posta',
  codeLabel: 'Kod',
  emailMeACode: 'Bana bir kod gönderin',
  working: 'Bir saniye…',
  sendNewCode: 'Yeni kod gönder',
  codeSent: 'E-postana bak. Kod 10 dakika geçerli.',
  notNow: 'Şimdi değil',
  badEmail: 'Bu bir e-posta adresine benzemiyor.',
  badCode: 'Bu kod işe yaramadı. Kodlar 10 dakikada dolar ve bir kez kullanılır.',
  serverBusy: 'Sunucuya ulaşılamadı. Birazdan tekrar dene.',
  serverDown: 'Sunucuya ulaşılamadı.',
  offline: 'Çevrimdışı',
  leaderboardTitle: 'Sıralama',
  leaderboardEmpty: 'Bu sıralamada henüz oyun yok. İlk sen ol.',
  leaderboardWouldBe: 'Bu oyun nerede yer alırdı',
  leaderboardThisGame: 'Bu oyun',
  leaderboardRanksHere: 'Bu oyun nerede yer alıyor',
  leaderboardOutside: 'İlk beşin dışında.',
  tutAccountTitle: 'Puanlarını sakla',
  tutAccountBody: 'Hesap oyunlarını kaydeder, puanlarını sıralamaya koyar ve profilini saklar.',
  ssoCancelled: 'Giriş iptal edildi.',
  ssoExpired: 'Çok uzun sürdü. Tekrar giriş yap.',
  ssoNoUsername: 'Hesap oluşturma tamamlanamadı. Birazdan tekrar dene.',
  ssoFailed: 'Olmadı. Tekrar dene, ya da e-postanı kullan.',
  tabProfile: 'Profil',
  countryAny: 'Söylemeyeyim',
  findCountry: 'Ülke ara',
  usernameLabel: 'Kullanıcı adı',
  bioLabel: 'Hakkımda',
  countryLabel: 'Ülke',
  bioHint: 'Bağlantı yok. {left}/{max}',
  save: 'Kaydet',
  saving: 'Kaydediliyor…',
  saved: 'Kaydedildi.',
  nameTaken: 'Bu ad birinde var.',
  nameTooShort: 'Biraz daha uzun olsun.',
  nameTooLong: 'Çok uzun.',
  nameBadCharacters: 'Yalnızca harf, rakam, kısa çizgi ve alt çizgi.',
  nameBadEdges: 'Bir harf veya rakamla başlayıp bitmeli.',
  nameMixedScripts: 'Tek bir alfabe seç ve ondan şaşma.',
  nameReserved: 'Bu bizim dağıttığımız adlara benziyor. Başka seç.',
  nameUnusable: 'Bu ad kullanılamaz.',
  bioTooLong: 'Hakkımda en çok {max} karakter alır.',
  bioHasLink: 'Burada bağlantıya izin yok.',
  bioHasControl: 'Burada bulunamayacak karakterler içeriyor.',
  bioUnusable: 'Bu metin kullanılamaz.',
  gamesHeading: 'Oyunlar',
  gamesEmpty: 'Burada henüz bir şey yok. Giriş yapmışken oynadığın oyunlar burada görünür.',
  gamesEmptyShort: 'Burada henüz bir şey yok.',
  gamesLoading: 'Oyunların okunuyor…',
  theirGamesLoading: 'Oyunları okunuyor…',
  profileLoading: 'Profil okunuyor…',
  playerNotFound: 'Burada bu adda kimse yok.',
  columnWhen: 'Ne zaman',
  columnRounds: 'tur',
  columnGame: 'Oyun',
  gameLoading: 'Oyun okunuyor…',
  gameUnreadable: 'Bu oyun okunamadı.',
  gameNotRanked: 'Değiştirilmiş kurallarla oynandı, bu yüzden sıralamaya girmiyor.',
  detailNotKept: 'Bu oyunun kelimeleri ve tahtaları artık saklanmıyor.',
  openingBoard: 'Açılış tahtası',
  roundByRound: 'Tur tur',
  roundNothing: 'bir şey bulunamadı',
  changeReplaced: '{letter} {slot}. yuvada değişti',
  changeWildOn: '{letter} {slot}. yuvada joker',
  changeWildOff: '{letter} joker {slot}. yuvadan gitti',
  keepThisGame: 'Bu oyunu sakla',
  savedToYourGames: 'Oyunlarına kaydedildi.',

  reportThis: 'Bildir',
  reportHeading: 'Bunu bildir',
  reportTheName: 'Ad',
  reportTheBio: 'Tanıtım',
  reportTheScore: 'Puan',
  reportWhy: 'Nesi yanlış? İsteğe bağlı.',
  reportSend: 'Bildirimi gönder',
  reportSent: 'Teşekkürler. Biri buna bakacak.',
  reportSignIn: 'Bir şey bildirmek için giriş yap.',
  reportGone: 'O artık yok.',
  reportCancel: 'Vazgeç',

  deleteAccount: 'Hesabımı sil',
  deleteAccountWhat: 'Bu, profilini ve sakladığın her oyunu kalıcı olarak kaldırır. Geri alınamaz.',
  deleteAccountAsk: 'Bana bir kod gönder',
  deleteConfirm: 'Kalıcı olarak sil',
  deleteGone: 'Hesabın gitti. Oynadığın için teşekkürler.',
  deleteNoAddress:
    'Bu hesapta doğrulanmış bir adres yok, bu yüzden silme kontrol edilemez. Bize yaz, elle yapalım.',

  rulesetFacts: 'Kelimeler {letters} · her {seconds} yeni bir taş',
  plurals: {
    words: { one: '{n} kelime', other: '{n} kelime' },
    rounds: { one: '{n} tur', other: '{n} tur' },
    flips: { one: '{n} çevirme', other: '{n} çevirme' },
    ticks: { one: '{n} vuruş', other: '{n} vuruş' },
    points: { one: '{n} puan', other: '{n} puan' },
    minLetters: { one: 'en az {n} harfli', other: 'en az {n} harfli' },
    seconds: { one: '{n} saniyede', other: '{n} saniyede' },
  },
}
